import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {ObjectNode} from "#template/nodes/object/object-node"
import {z} from "zod/v4-mini"
import type {ObjectNodeDefinition, ObjectNodeParameters} from "#template/nodes/object/types"
import type {OnCompileContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {objectNodeParameters} from "#template/nodes/object/validation"
import type {ExternalId} from "#template/nodes/types"
import {externalId} from "#template/nodes/validation"
import type {SceneNodes} from "#template/interfaces/scene-object"
import {visitNone} from "#template/template-node/visit-helpers"

export type AnnotationParameters = {
    name: string
    id: ExternalId
    label: string
    description: string
} & ObjectNodeParameters

const annotationqDefinition: ObjectNodeDefinition<AnnotationParameters> = {
    nodeClass: "Annotation",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                id: externalId,
                label: z.string(),
                description: z.string(),
            }),
            objectNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class Annotation extends ObjectNode<AnnotationParameters> {
    public override get definition() {
        return annotationqDefinition
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<AnnotationParameters> {
        const {evaluator, sceneProperties} = context

        if (sceneProperties?.parameters.showAnnotations === false) return skipped

        const {label, description, id} = this.parameters

        const scope = evaluator.getScope(this)

        this.setupObject(scope, context, "Annotation", undefined, undefined, (objectProps) => {
            return scope.struct<SceneNodes.Annotation>("Annotation", {
                type: "Annotation",
                ...objectProps,
                label,
                description,
                annotationID: id,
            })
        })

        return visitNone(this.parameters)
    }
}
